import { clamp, motion } from 'framer-motion'
import { useIntersectionObserver } from 'usehooks-ts'

import Counter from '@/components/counter'
import RichText from '@/components/ui/rich-text'
import ImageContentful from './ui/image-contentful'
import { cx } from '@/utils/strings'
import { TypeSectionBarGraph, TypeComponentCtaLink } from '@/types/ctf'
import { useInspectorProps } from '@/utils/live-preview'
import { isPrint } from '@/utils/print'

type BarGraphFields = TypeSectionBarGraph<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']

export type AverageMarkerProps = {
  averageValue: number
  index: number
  size?: BarGraphFields['size']
}

export function AverageMarker({ averageValue, index, size = 'Large' }: AverageMarkerProps) {
  return (
    <motion.div
      className={cx('w-[2px] flex flex-col items-center absolute top-[-26px] z-10')}
      style={{ left: `${averageValue}%` }}
      variants={{
        0: { opacity: 0, translateY: -30, translateX: '-50%' },
        100: { opacity: 1, translateY: 0, translateX: '-50%' },
      }}
      transition={{ duration: 0.4, ease: 'easeOut', delay: 1.5 + (index + 1) * 0.1 }}
    >
      <span className={cx('text-eyebrow uppercase whitespace-nowrap')}>AVG: {averageValue}%</span>
      <div
        className={cx(
          'w-[3px] rounded-full bg-accent-teal',
          size === 'Small' && 'h-[34px]',
          size === 'Medium' && 'h-[47px]',
          size === 'Large' && 'h-[59px]'
        )}
      />
    </motion.div>
  )
}

export type BarGraphItemProps = {
  id?: string
  value: number
  averageValue?: number
  logoIcon?: any
  index: number
  isActive?: boolean
  size?: BarGraphFields['size']
}

export function BarGraphItem({ value, averageValue, index, logoIcon, isActive, size = 'Large' }: BarGraphItemProps) {
  return (
    <div className="flex items-center">
      {logoIcon && (
        <div className="flex justify-center items-center w-8 md:w-12 mr-4 md:mr-6">
          <ImageContentful {...logoIcon.fields} />
        </div>
      )}
      <div className="relative flex items-center flex-1">
        {averageValue && <AverageMarker averageValue={averageValue} index={index} size={size} />}
        <div
          className={cx(
            'relative w-full rounded-lg overflow-hidden bg-secondary-50 dark:bg-secondary-800',
            size === 'Small' && 'h-[24px]',
            size === 'Medium' && 'h-[36px]',
            size === 'Large' && 'h-[48px]'
          )}
        >
          <motion.div
            style={{ width: `${value}%` }}
            // `bg-primary-500` is a solid fallback beneath the gradient: print engines don't reliably render
            // gradient background-images, so the solid color guarantees a visible bar in PDFs (hidden by the
            // gradient on screen).
            className={cx(
              'absolute top-0 left-0 h-full bg-primary-500 bg-gradient-to-r from-bw-deep-blue to-primary-500 origin-left'
            )}
            variants={{
              0: { translateX: '-100%' },
              100: { translateX: 0 },
            }}
            transition={{ duration: 1.5, ease: 'anticipate' }}
          />
        </div>
      </div>
      <div className="relative ml-4">
        <Counter
          className={cx(
            'absolute top-0 bottom-0 left-0 right-0 m-auto w-full',
            size === 'Small' && 'h-[30px] text-lg',
            size === 'Medium' && 'h-[32px] text-xl',
            size === 'Large' && 'h-[40px] text-2xl'
          )}
          from={0}
          to={value}
          duration={1.5}
          postfix="%"
          isActive={isActive}
        />
      </div>
    </div>
  )
}

export type BarGraphProps = BarGraphFields & {
  entryId?: string
  eyebrowText?: string
  subtext?: string
  ctas?: TypeComponentCtaLink<'WITHOUT_UNRESOLVABLE_LINKS'>[]
}

export default function BarGraph({ entryId, headline, stats: items, size = 'Large', bottomCaption }: BarGraphProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  // scale the intersection threshold based on the number of items
  const threshold = clamp(0.01, 0.5, 0.5 - ((items?.length ?? 0) - 3) * 0.04)
  const { entry, ref } = useIntersectionObserver({
    threshold,
    freezeOnceVisible: true,
  })
  // In print there's no scroll to trigger the reveal, so render fully-visible from the start (initial "100"
  // below), which snaps bars, markers and counters to their final static state with no animation.
  const isPrintView = isPrint()
  const isVisible = isPrintView || !!entry?.isIntersecting
  // Average markers sit above the bars (top-[-26px]); they need top headroom that the stripped print
  // container would otherwise clip.
  const hasAverageMarker = items?.some(
    (item) => !!item && !!(item.fields.averageValue || item.fields.averageValue2 || item.fields.averageValue3)
  )

  return (
    <div
      ref={ref}
      className={cx(
        // Screen: bordered, rounded, padded card. Print: strip the container chrome for a clean, flush graph,
        // but keep top room when average markers are present (they render above the bars).
        !isPrintView && 'border rounded-lg pt-8',
        isPrintView && hasAverageMarker && 'pt-8',
        // The bars, track and marker are CSS backgrounds, which browsers omit when printing unless
        // `print-color-adjust: exact` is set (inherited, so one declaration covers all descendants).
        '[print-color-adjust:exact] [-webkit-print-color-adjust:exact]'
      )}
      {...pageInspectorProps({ fieldId: 'internalName' })}
    >
      {headline && (
        <div
          className={cx(
            !isPrintView && 'text-center px-8',
            size === 'Small' && 'mb-4',
            size === 'Medium' && 'mb-8',
            size === 'Large' && 'mb-12'
          )}
        >
          <h3 className="h3 header-gradient">{headline}</h3>
        </div>
      )}
      <motion.div
        className={cx(
          'flex flex-col pr-[90px] pl-[40px] lg:pl-[120px] lg:pr-[160px]',
          // The large viewport-based side padding collapses the flex-1 track inside the narrow print column,
          // squashing bars to ~1px. In print drop the outer padding; keep only right room for the counter number.
          isPrintView && 'pl-0! pr-16! pb-0!',
          size === 'Small' && 'pb-[24px] gap-4 lg:gap-6',
          size === 'Medium' && 'pb-[36px] gap-6 lg:gap-8',
          size === 'Large' && 'pb-[77px] gap-4 lg:gap-8'
        )}
        transition={{ staggerChildren: 0.1 }}
        initial={isPrintView ? '100' : '0'}
        animate={isVisible ? '100' : '0'}
      >
        {items?.map((item, index) => {
          if (!item) return null
          const {
            fields: {
              label,
              value,
              averageValue,
              averageValue2,
              averageValue3,
              logoIcon,
              logoIcon2,
              logoIcon3,
              value2,
              value3,
            },
            sys: { id },
          } = item
          return (
            <div key={id || label}>
              <h4 className={cx('py-0', size === 'Small' && 'text-lg', logoIcon && 'ml-8 md:ml-[72px]')}>{label}</h4>
              <div
                className={cx(
                  'flex flex-col',
                  averageValue
                    ? cx(size === 'Small' && 'pt-4', (size === 'Medium' || size === 'Large') && 'pt-6')
                    : cx(size === 'Small' && 'pt-2', (size === 'Medium' || size === 'Large') && 'pt-4'),
                  averageValue2 || averageValue3 ? 'gap-y-8' : 'gap-y-4'
                )}
              >
                <BarGraphItem
                  index={index}
                  value={value}
                  averageValue={averageValue}
                  logoIcon={logoIcon}
                  isActive={isVisible}
                  size={size}
                />
                {value2 && (
                  <BarGraphItem
                    index={index}
                    value={value2}
                    averageValue={averageValue2}
                    logoIcon={logoIcon2}
                    isActive={isVisible}
                    size={size}
                  />
                )}
                {value3 && (
                  <BarGraphItem
                    index={index}
                    value={value3}
                    averageValue={averageValue3}
                    logoIcon={logoIcon3}
                    isActive={isVisible}
                    size={size}
                  />
                )}
              </div>
            </div>
          )
        })}
      </motion.div>

      {bottomCaption?.content && (
        <div className="text-center pb-4 [a]:text-tealBlue">
          <RichText body={bottomCaption} />
        </div>
      )}
    </div>
  )
}
